import { formatDateTime } from '../../utils/formatDate';
import { deviceLines, startupLines } from '../support/diagnostics';
import { isStreamConnected } from '../../db/replication';
import type { SyncErrorEntry } from './syncErrorLog';
import type { PendingComparison, PendingDoc } from './pendingQueue';

/** What the app knows about itself when the report is written. */
export interface SyncReportContext {
    /** Who is signed in, as 'Name (login)'. */
    user: string;
    online: boolean;
    /** Documents still waiting to go up. */
    pending: number;
    /** Colleagues whose queued work awaits their own sign-in. */
    blockedBy: string[];
}

/**
 * The whole sync situation as one block of text to paste into a message.
 *
 * Deliberately NOT translated. It is written to be forwarded to whoever fixes
 * the app, so it has to read the same whatever language the device is set to,
 * and its field names have to match what the code calls things.
 *
 * Everything here is state the user cannot otherwise see and cannot be expected
 * to recount: which collection failed, how often, the server's own words, and
 * the version and connection it happened on.
 */
export function buildSyncReport(
    context: SyncReportContext,
    errors: SyncErrorEntry[],
    /**
     * The queue document by document. A bare count says nothing about WHICH
     * documents are waiting — and when the count is the whole complaint, the
     * entries are the only thing worth reporting.
     */
    queue: PendingDoc[] = [],
    /** What the server holds for each of them, when that was asked. */
    comparison: Map<string, PendingComparison> | null = null
): string {
    const lines = [
        'AST Data — Synchronisierungsbericht',
        // Version, device and address are the same in every report the app
        // sends, and are shared with them (features/support/diagnostics).
        ...deviceLines(),
        `Benutzer: ${context.user || '—'}`,
        `Verbindung: ${context.online ? 'online' : 'offline'}, Datenstrom ${
            isStreamConnected() ? 'verbunden' : 'getrennt'
        }`,
        `Warteschlange: ${context.pending}`,
        `Wartet auf Anmeldung: ${
            context.blockedBy.length > 0 ? context.blockedBy.join(', ') : '—'
        }`,
        '',
        // How long the app took to start: asked about as often as the sync.
        ...startupLines(),
        '',
        `Fehler: ${errors.length}`
    ];

    if (queue.length > 0) {
        lines.push('', 'Warteschlange im Einzelnen:');
        for (const doc of queue) {
            const verdict = comparison?.get(`${doc.collection}/${doc.docId}`);
            lines.push(
                `- ${doc.collection}/${doc.docId} · ${doc.state}${
                    doc.updatedAt ? ` · ${formatDateTime(doc.updatedAt)}` : ''
                } · Autor ${doc.userId || '—'}${
                    verdict ? ` · Server: ${verdict.result}` : ''
                }${
                    verdict && verdict.differing.length > 0
                        ? ` (${verdict.differing.join(', ')})`
                        : ''
                }${
                    doc.rejected
                        ? ` · vom Server zurueckgewiesen ${doc.rejected.count}x, zuletzt ${formatDateTime(doc.rejected.lastAt)}`
                        : ''
                }`
            );
        }
    }

    for (const error of errors) {
        lines.push(
            '',
            `- ${formatDateTime(error.lastAt)} · ${error.collection}${
                error.direction ? ` · ${error.direction}` : ''
            }${error.count > 1 ? ` · ${error.count}×, erstmals ${formatDateTime(error.firstAt)}` : ''}`,
            `  ${error.message}`
        );
        if (error.code !== '') {
            lines.push(`  Code: ${error.code}`);
        }
        for (const detail of error.details) {
            lines.push(`  ${detail}`);
        }
    }

    return lines.join('\n');
}

/**
 * Put the report on the clipboard, or say that it could not be.
 *
 * The field app is opened over plain HTTP often enough (a tablet on the local
 * WLAN) and `navigator.clipboard` does not exist outside a secure context. The
 * old `execCommand` path still works there, and when even that fails the dialog
 * leaves the text on screen to be selected by hand.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {
        // Blocked or unavailable — fall through to the old way.
    }
    try {
        const area = document.createElement('textarea');
        area.value = text;
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        const copied = document.execCommand('copy');
        document.body.removeChild(area);
        return copied;
    } catch {
        return false;
    }
}
